import '../../../../assets/stylesheets/Units/Calendar.scss';
import 'react-dates/lib/css/_datepicker.css';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { DateRangePicker, isInclusivelyBeforeDay } from 'react-dates';
import 'react-dates/initialize';
import { VERTICAL_ORIENTATION, OPEN_DOWN, START_DATE } from 'react-dates/constants';
import CalendarHeader from './CalendarHeader';
import moment from 'moment';

const navigateCalendar = (setSince, setTill, setTempSince, setTempTill, callback, since, till, direction) => {
    let updatedSince = null;
    let updatedTill = null;
    if (direction) {
        // right
        const limit = moment();
        updatedTill = till.clone().add(1, 'month');
        if (updatedTill.isAfter(limit)) updatedTill = limit;
        updatedSince = updatedTill.clone().subtract(1, 'month');
    } else {
        updatedSince = since.clone().subtract(1, 'month');
        updatedTill = till.clone().subtract(1, 'month');
    }
    setSince(updatedSince);
    setTill(updatedTill);
    setTempSince(updatedSince);
    setTempTill(updatedTill);
    if (callback) callback();
}

const getOnDatesChange = (setTempSince, setTempTill, startDate, endDate, tempSince, tempTill) => {
    if (startDate) setTempSince(startDate);
    if (endDate) setTempTill(endDate);

    if (tempTill && (tempTill < endDate)) {
        setTempSince(endDate);
        setTempTill(null);
    }

    if ((startDate < tempSince) && (tempSince < endDate) && (tempSince < tempTill)) {
        setTempSince(startDate);
        setTempTill(null);
    }

    if (!tempTill) {
        setTempTill(endDate);
    }

    if (startDate && !endDate && tempTill) {
        setTempTill(null);
    }
}

const isOutsideRange = day => !isInclusivelyBeforeDay(day, moment());

const cancelCalendar = (setFocusedInput, setTempSince, setTempTill, since, till) => {
    setFocusedInput(null);
    setTempSince(since.clone());
    setTempTill(till.clone());
}

const confirmCalendar = (setFocusedInput, setSince, setTill, tempSince, tempTill, callback) => {
    setFocusedInput(null);
    setSince(tempSince.clone());
    setTill((tempTill || tempSince).clone());
    if (callback) callback();
}

const CalendarBody = ({
    since,
    setSince,
    till,
    setTill,
    focusedInput,
    setFocusedInput,
    onChange,
    hideInputs,
    currentRange
}) => {
    const prevFocusedInput = useRef(null);
    const elem = useRef(null);
  
    const [tempSince, setTempSince] = useState(since.clone());
    const [tempTill, setTempTill] = useState(till.clone());

    const onDatesChange = useCallback(
        ({ startDate, endDate }) => getOnDatesChange(setTempSince, setTempTill, startDate, endDate, tempSince, tempTill),
        [tempSince, tempTill, setTempSince, setTempTill]
    );
    
    const onCancelCalendar = useCallback(
        () => cancelCalendar(setFocusedInput, setTempSince, setTempTill, since, till), 
        [setFocusedInput, setTempSince, setTempTill, since, till],
    );
    const onConfirmCalendar = useCallback(
        () => confirmCalendar(setFocusedInput, setSince, setTill, tempSince, tempTill, onChange),
        [setFocusedInput, setSince, setTill, tempSince, tempTill, onChange],
    );

    const navigateLeft = useCallback(
        () => navigateCalendar(setSince, setTill, setTempSince, setTempTill, onChange, since, till, 0), 
        [setSince, setTill, setTempSince, setTempTill, onChange, since, till],
    );
    const navigateRight = useCallback(
        () => navigateCalendar(setSince, setTill, setTempSince, setTempTill, onChange, since, till, 1),
        [setSince, setTill, setTempSince, setTempTill, onChange, since, till],
    );

    const onFocusChange = useCallback(
        (newFocusedInput) => {
            if (!prevFocusedInput.current && newFocusedInput) setFocusedInput(START_DATE);
            else setFocusedInput(newFocusedInput);
        },
        [focusedInput, prevFocusedInput, setFocusedInput],
    );

    useEffect(() => {
        if (prevFocusedInput.current || !focusedInput) return;
        if (!elem.current) return;
        const grid = elem.current.querySelector('.CalendarMonthGrid');
        if (!grid) return;
        grid.scrollTop = grid.scrollHeight;
    }, [focusedInput]);

    useEffect(() => {
        prevFocusedInput.current = focusedInput;
    });

    useEffect(() => {
        setTempSince(currentRange.startRange);
        setTempTill(currentRange.endRange);
    }, [currentRange]);

    return (
        <div className={hideInputs ? `calendar calendar--hidden` : `calendar`} ref={elem}>
            <div className={`calendarNav calendarLeft`} onClick={navigateLeft}>
                <i className={`fa fa-chevron-left`} />
            </div>
            <DateRangePicker
                autoFocus={false}
                orientation={VERTICAL_ORIENTATION}
                focusedInput={focusedInput}
                onFocusChange={onFocusChange}
                onDatesChange={onDatesChange}
                startDateId="datePickerStartDate"
                endDateId="datePickerEndDate"
                openDirection={OPEN_DOWN}
                startDate={tempSince}
                endDate={tempTill}
                isDayBlocked={isOutsideRange}
                isOutsideRange={isOutsideRange}
                hideKeyboardShortcutsPanel
                displayFormat="D MMM YYYY"
                numberOfMonths={13}
                minimumNights={0}
                maxDate={moment()}
                initialVisibleMonth={() => moment().subtract(12, 'month')}
                keepOpenOnDateSelect
                small
                noBorder
                navPrev={<span />}
                navNext={<span />}
                renderCalendarInfo={() => (<CalendarHeader onCancel={onCancelCalendar} onConfirm={onConfirmCalendar} />)}
            />
            <div className={`calendarNav calendarRight`} onClick={navigateRight}>
                <i className={`fa fa-chevron-right`} />
            </div>
        </div>
    );
};

export default CalendarBody;